import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  ArrowRight, 
  X,
  CreditCard,
  Building,
  Wallet,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useMerchantStatus } from '@/hooks/useMerchantStatus';
import { useAuth } from '@/context/AuthContext';

interface PayoutSetupReminderProps {
  onDismiss?: () => void;
}

const PayoutSetupReminder: React.FC<PayoutSetupReminderProps> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const status = useMerchantStatus();
  const [dismissed, setDismissed] = useState(false);

  // Check if already dismissed today
  React.useEffect(() => {
    if (user) {
      const dismissedKey = `payout_reminder_dismissed_${user._id}_${new Date().toDateString()}`;
      const wasDismissed = sessionStorage.getItem(dismissedKey);
      if (wasDismissed) {
        setDismissed(true);
      }
    }
  }, [user]);

  const handleDismiss = () => {
    if (user) {
      const dismissedKey = `payout_reminder_dismissed_${user._id}_${new Date().toDateString()}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleSetupClick = () => {
    navigate('/merchant-profile#payout-methods');
  };

  // Don't show if:
  // - Not a merchant
  // - Already has payout method
  // - Still loading
  // - Was dismissed
  // - No balance to withdraw
  if (!user?.roles?.includes('MERCHANT') || 
      status.hasPayoutMethod || 
      status.loading || 
      dismissed ||
      (!status.balance?.available && !status.balance?.pending)) {
    return null;
  }

  const totalBalance = (status.balance?.available || 0) + (status.balance?.pending || 0);

  return (
    <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {totalBalance > 0 
                    ? `You have ${status.balance?.currency || 'USD'} ${totalBalance.toFixed(2)} waiting!`
                    : 'Set Up Payouts to Receive Earnings'
                  }
                </h3>
                <p className="text-sm text-gray-600">
                  Link a payout method to withdraw your marketplace earnings
                </p>
              </div>
            </div>

            {/* Balance Display */}
            {totalBalance > 0 && (
              <div className="bg-white rounded-lg p-4 mb-4 border border-amber-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Available to Withdraw</p>
                    <p className="text-xl font-bold text-green-600">
                      {status.balance?.currency} {status.balance?.available?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-xl font-bold text-amber-600">
                      {status.balance?.currency} {status.balance?.pending?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payout Options */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Available Payout Methods:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-white">
                  <CreditCard className="w-3 h-3 mr-1" />
                  Stripe Connect
                </Badge>
                <Badge variant="outline" className="bg-white">
                  <Building className="w-3 h-3 mr-1" />
                  Bank Account
                </Badge>
                <Badge variant="outline" className="bg-white">
                  <Wallet className="w-3 h-3 mr-1" />
                  PayPal
                </Badge>
              </div>
            </div>

            {/* Info Alert */}
            <Alert className="bg-blue-50 border-blue-200 mb-4">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-800">
                You can continue accepting orders and building your balance. 
                Add a payout method whenever you're ready to withdraw.
              </AlertDescription>
            </Alert>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={handleSetupClick}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Set Up Payout Method
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-700"
              >
                Remind me tomorrow
              </Button>
            </div>
          </div>

          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="ml-4 -mt-2 -mr-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayoutSetupReminder;
